import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
import FileSaver from "file-saver";
import { pdf } from "@react-pdf/renderer";
import { BASE_API_URL, STATUS } from "./constants";
import { TimeSlot } from "../types/Availability";
import { CDN_URL, S3_BASE_URL } from "../info";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type RangeType = "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth";

interface DecodedToken {
  exp: number; // expiration time as UNIX timestamp
}

interface ValidationOptions {
  required?: boolean;
  minLength?: number;
  context?: "signup" | "login";
}
interface CustomJwtPayload extends JwtPayload {
  firstName: string;
  lastName: string;
  id: number;
  vendorName: string;
  company: string;
  role: string;
  vendorImage?: string;
  email: string;
  serviceTypeId: number;
  isDistanceRangApplicable: boolean;
}

export const getTokenExpiration = (token: string): number | null => {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.exp;
  } catch (e) {
    return null;
  }
};

export const validateFileType = (file: File, allowedTypes: string[]) => {
  const fileType = file.type;
  return allowedTypes.includes(fileType);
};

export async function refreshToken(
  email: string,
  password: string,
  isLoginForVendor: boolean
): Promise<string> {
  console.log("Token expired, refreshing");
  const apiPath = isLoginForVendor ? "vendor/login" : "admin/login";

  const response = await fetch(BASE_API_URL + `/api/v1/${apiPath}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });
  if (response.ok) {
    const data = await response.json();
    const newToken = data.token;
    return newToken;
  } else {
    throw new Error(`Refresh token failed. Logging out..`);
  }
}

export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export function formatCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

export function setNumberPrecision(value: number): number {
  return Math.round(value * 100) / 100;
}

export function truncateString(str: string, maxLength: number = 10): string {
  return str && str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
}

export const formatTitle = (pathname: string) => {
  const segments = pathname.replace(/^\/|\/$/g, "").split("/");

  const formattedSegments = segments
    .map((segment, index) => {
      if (/\d/.test(segment)) {
        return "";
      }

      return (
        segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ")
      );
    })
    .filter(Boolean);

  return formattedSegments.join(" ");
};

export function validateField(
  type:
    | "email"
    | "password"
    | "text"
    | "number"
    | "phoneNumber"
    | "boolean"
    | "zipCode"
    | "website"
    | "quantity"
    | "otp",
  value: string | number | boolean,
  options: ValidationOptions = {}
): string | null {
  const { required = false, minLength, context = "signup" } = options;

  if (required && !value) {
    return "This field is required";
  }

  if (type === "password" && typeof value === "string") {
    if (context === "signup") {
      if (minLength && value.length < minLength) {
        return `Password must be at least ${minLength} characters long`;
      }
      if (!/[a-z]/.test(value) || !/[A-Z]/.test(value)) {
        return "Password must include both upper and lower case characters";
      }
    } else {
      if (!value) {
        return "Password cannot be empty";
      }
    }
  }

  if (
    type === "email" &&
    typeof value === "string" &&
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
  ) {
    return "Please enter a valid email";
  }

  if (
    type === "phoneNumber" &&
    typeof value === "string" &&
    !/^\d{10}$/.test(value)
  ) {
    return "Phone number must be exactly 10 digits.";
  }

  if (
    type === "website" &&
    typeof value === "string" &&
    !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/.test(
      value
    )
  ) {
    return "Please enter a valid website URL";
  }

  if (type === "quantity" && typeof value === "number" && value < 0) {
    return "Please enter a valid quantity";
  }

  if (type === "otp" && typeof value === "string" && value.length !== 6) {
    return "Please enter a valid OTP";
  }

  return null;
}

export const getFieldType = (
  fieldName: string
): "text" | "number" | "quantity" => {
  switch (fieldName) {
    case "productName":
    case "description":
    case "sku":
      return "text";
    case "price":
      return "number";
    case "quantityAvailable":
      return "quantity";
    default:
      return "text";
  }
};

export function getDateRange(rangeType: RangeType, date: Date = new Date()) {
  const startDate = new Date(date);
  const endDate = new Date(date);

  switch (rangeType) {
    case "thisWeek":
    case "lastWeek":
      const weekOffset = rangeType === "lastWeek" ? 7 : 0;
      startDate.setDate(date.getDate() - date.getDay() - weekOffset);
      endDate.setDate(startDate.getDate() + 6);
      break;

    case "thisMonth":
      startDate.setDate(1);
      endDate.setMonth(date.getMonth() + 1, 0);
      break;

    case "lastMonth":
      startDate.setMonth(date.getMonth() - 1, 1);
      endDate.setDate(0);
      break;

    default:
      throw new Error("Invalid range type");
  }

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

export function tokenDecoder(token: any) {
  if (typeof token !== "string" || !token) {
    return {
      email: "",
      vendorId: 0,
      role: "",
      serviceTypeId: 0,
      isDistanceRangApplicable: false,
    };
  }
  const {
    email,
    id: vendorId,
    role,
    serviceTypeId,
    isDistanceRangApplicable,
  } = jwtDecode<CustomJwtPayload>(token);
  return { email, vendorId, role, serviceTypeId, isDistanceRangApplicable };
}

export const validatePasswordMatch = (
  password: string,
  confirmPassword: string
) => {
  return password !== confirmPassword;
};

export const getWeekNumber = (date: Date) =>
  Math.ceil(
    ((date.getTime() - new Date(date.getUTCFullYear(), 0, 1).getTime()) /
      86400000 +
      ((date.getUTCDay() + 6) % 7) +
      1) /
      7
  );

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export default function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timerRef = useRef<any | null>(null);

  useEffect(() => {
    // This function updates the debounced value after the delay
    const debounceHandler = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
    };

    // Start the debounce process when the value changes
    debounceHandler();

    // Cleanup function to clear the timer on component unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

export function formatDate(date: any, formatString: string) {
  if (!date) return "";
  return format(new Date(date), formatString);
}

export function formatRouteString(route: string): string {
  const baseRoute = route.split("/")[0];
  return baseRoute.replace(/-/g, " ");
}

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function generateDateRange(
  startDate: Date,
  endDate: Date,
  interval: number = 1
): Date[] {
  let dateList = [];
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dateList.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + interval);
  }
  return dateList;
}

export const generateMonthRange = (
  startDate: Date,
  endDate: Date,
  interval: number = 1
) => {
  const months: string[] = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const monthName = monthNames[currentDate.getMonth()];
    if (!months.includes(monthName)) {
      months.push(monthName);
    }
    currentDate.setMonth(currentDate.getMonth() + interval);
  }
  return months;
};

export const aggregateByMonth = (
  data: any[],
  rangeStart: Date,
  rangeEnd: Date
) => {
  const monthlyData: any = {};
  const allMonths = generateMonthRange(rangeStart, rangeEnd);

  allMonths.forEach((month) => {
    monthlyData[month] = 0;
  });

  data.forEach((item) => {
    const date = new Date(item.period);
    const monthName = monthNames[date.getMonth()];
    if (monthlyData[monthName] !== undefined) {
      monthlyData[monthName] += item.totalSales;
    }
  });

  return allMonths.map((monthName) => ({
    period: monthName,
    totalSales: monthlyData[monthName],
  }));
};

export const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

export const useDecodedTokenResult = () => {
  const token = useSelector((state: any) => state.auth.token);
  return tokenDecoder(token);
};

export function autoFormatDate(date: string): string {
  const now = moment();
  const momentDate = moment(date);

  if (
    momentDate.isSameOrBefore(now, "day") &&
    momentDate.isAfter(now.clone().subtract(3, "days"))
  ) {
    return momentDate.fromNow();
  } else {
    return momentDate.format("DD/MM/YYYY");
  }
}

export const isSameDay = (timestamp1: number, timestamp2: number) => {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

export const downloadPdfFile = async (data: any, fileName: string) => {
  const blob = await pdf(data).toBlob();
  FileSaver.saveAs(blob, fileName);
};

export function autoFormatNumber(number: number): string {
  if (number >= 1_000_000_000) {
    return (number / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  } else if (number >= 1_000_000) {
    return (number / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (number >= 1_000) {
    return (number / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  } else {
    return number.toString();
  }
}

export const formatScheduleDateTime = (
  dateTime: string,
  hours?: number
): string => {
  if (!dateTime) return "- - -";

  try {
    const date = moment(dateTime);
    const endTime = moment(dateTime).add(hours || 3, "hour");

    const startTimeFormat = date.format("ha");
    const endTimeFormat = endTime.format("ha");

    const formattedDate = date.format("ddd, MMM D");

    return `${formattedDate}, ${startTimeFormat}-${endTimeFormat}`;
  } catch (error) {
    console.error("Date formatting error:", error);
    return "- - -";
  }
};

export function formatDateTime(date: any, formatString: string) {
  if (!date) return "";
  return moment(date).format(formatString);
}

export const convertTo24Hour2 = (time: string | null) => {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) {
    return null;
  }
  return time;
};

// Check if there's an overlap within time slots for a single day
export const checkForOverlaps = (timeSlots: any) => {
  for (let i = 0; i < timeSlots.length; i++) {
    const start1 = convertTo24Hour2(timeSlots[i].start);
    const end1 = convertTo24Hour2(timeSlots[i].end);

    // Skip invalid or empty time slots
    if (!start1 || !end1 || start1 >= end1) {
      // console.log("Invalid time range detected:", start1, end1);
      return true; // Invalid time range
    }

    for (let j = i + 1; j < timeSlots.length; j++) {
      const start2 = convertTo24Hour2(timeSlots[j].start);
      const end2 = convertTo24Hour2(timeSlots[j].end);

      // Skip invalid or empty time slots
      if (!start2 || !end2 || start2 >= end2) {
        // console.log("Invalid time range detected:", start2, end2);
        return true; // Invalid time range
      }

      if (
        new Date(`1970-01-01T${end1}`).getTime() >
          new Date(`1970-01-01T${start2}`).getTime() &&
        new Date(`1970-01-01T${start1}`).getTime() <
          new Date(`1970-01-01T${end2}`).getTime()
      ) {
        return true; // Overlap detected
      }
    }
  }
  return false; // No overlaps
};

export const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export function generateTimeSlots(interval: number = 15): string[] {
  const times: string[] = [];
  const totalMinutes = 24 * 60;

  for (let minutes = 0; minutes < totalMinutes; minutes += interval) {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

    times.push(
      `${displayHour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")} ${period}`
    );
  }

  return times;
}

export function convertTo24Hour(time12h: string): string {
  const [time, period] = time12h.split(" ");
  let [hours, minutes] = time.split(":");

  if (hours === "12") {
    hours = "00";
  }

  if (period === "PM") {
    hours = (parseInt(hours, 10) + 12).toString();
  }

  return `${hours.padStart(2, "0")}:${minutes}`;
}

export function convertTo12Hour(time24h: string): string {
  const [hours, minutes] = time24h.split(":");
  const hour = parseInt(hours, 10);

  if (hour === 0) {
    return `12:${minutes} AM`;
  }
  if (hour === 12) {
    return `12:${minutes} PM`;
  }
  if (hour > 12) {
    return `${(hour - 12).toString().padStart(2, "0")}:${minutes} PM`;
  }
  return `${hour.toString().padStart(2, "0")}:${minutes} AM`;
}

export function checkTimeOverlap(
  existingSlots: TimeSlot[],
  newSlot: TimeSlot
): boolean {
  const newStart = convertTo24Hour(newSlot.start);
  const newEnd = convertTo24Hour(newSlot.end);
  if (newStart >= newEnd) {
    return true;
  }

  return existingSlots.some((slot) => {
    const existingStart = convertTo24Hour(slot.start);
    const existingEnd = convertTo24Hour(slot.end);
    return (
      (newStart >= existingStart && newStart < existingEnd) ||
      (newEnd > existingStart && newEnd <= existingEnd) ||
      (newStart <= existingStart && newEnd >= existingEnd)
    );
  });
}

export function getCurrentUserTimeZone(): string {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone;
}

export async function saveAvailabilitySettings(data: {
  schedule: any[];
  blockedDates: string[];
}) {
  // Simulating API call with a delay
  await new Promise((resolve) => setTimeout(resolve, 1000));
  console.log("Saved availability settings:", data);
  return { success: true };
}

export const parseTime = (time: string) => {
  const [hours, minutes] = time.split(/[: ]/).map(Number);
  const isPM = time.toLowerCase().includes("pm");
  return new Date(
    0,
    0,
    0,
    isPM ? hours + (hours !== 12 ? 12 : 0) : hours % 12,
    minutes
  );
};
export function formatTimeSlotPayload(
  schedule: any,
  blockedDates: any,
  isForProduct: boolean,
  isEditingProduct: boolean
): {
  timeSlotsPayload: any[];
  adjustedBlockedDates: string[];
  updatedUnavailableSlotsIds: number[];
} {
  let updatedUnavailableSlotsIds: number[] = [];
  const timeSlotsPayload = schedule.flatMap((day: any) => {
    if (day.isAvailable) {
      return day.timeSlots
        .filter((slot: any) => {
          if (isForProduct && !isEditingProduct) {
            return !slot.productId;
          }
          return !slot.id;
        })
        .map((slot: any) => {
          const currentDay = moment().isoWeekday(day.day);
          const today = moment();
          const upcomingDate = currentDay.isBefore(today, "day")
            ? currentDay.add(1, "weeks")
            : currentDay;

          const startTime = moment(
            `${upcomingDate.format("YYYY-MM-DD")} ${slot.start}`,
            "YYYY-MM-DD hh:mm A"
          ).format("YYYY-MM-DDTHH:mm:ssZ");
          const endTime = moment(
            `${upcomingDate.format("YYYY-MM-DD")} ${slot.end}`,
            "YYYY-MM-DD hh:mm A"
          ).format("YYYY-MM-DDTHH:mm:ssZ");

          return {
            dayOfWeek: day.day,
            startTime,
            endTime,
          };
        });
    } else if (!day.isAvailable && day.timeSlots.length > 0 && !isForProduct) {
      const removedSlots = day.timeSlots.map((slot: any) => slot.id as number);
      updatedUnavailableSlotsIds = [
        ...updatedUnavailableSlotsIds,
        ...removedSlots,
      ];
      return [];
    }
    return [];
  });

  const adjustedBlockedDates: string[] = blockedDates
    .filter((blockedDate: any) => {
      if (isForProduct && !isEditingProduct) {
        return !blockedDate.productId;
      }
      return !blockedDate.id;
    })
    .map((blockedDate: any) => {
      const dateWithTimezone = moment(blockedDate.date).format(
        "YYYY-MM-DDTHH:mm:ssZ"
      );
      return dateWithTimezone;
    });

  return { timeSlotsPayload, adjustedBlockedDates, updatedUnavailableSlotsIds };
}

export const IsServiceTypeVendor = () => {
  const { serviceTypeId } = useDecodedTokenResult();
  if (serviceTypeId > 2) {
    return true;
  }
  return false;
};

export const determineOrderStatus = (
  currentStep: number,
  serviceTypeId: number,
  orders: any
): number => {
  if (currentStep === 4) {
    return serviceTypeId > 2 ? STATUS.OrderCompleted : STATUS.OrderDelivered;
  }

  switch (currentStep) {
    case 1:
      return STATUS.OrderActivated;
    case 2:
      return STATUS.OrderConfirmed;
    case 3:
      return STATUS.OrderDispatched;
    case 5:
      return STATUS.OrderReturned;
    case 6:
      return STATUS.OrderCancelled;
    default:
      return orders?.data?.data?.statusId; // Default to statusId or null if undefined
  }
};

export const formatNumber = (num: number) => {
  if (num >= 1000) {
    return Math.round(num / 1000) + "k";
  }
  return num.toFixed(0);
};
export const uploadToS3WithPost = async (
  file: File,
  url: string,
  fields: Record<string, string>,
  onProgress: (progress: number) => void,
  signal: AbortSignal
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const progress =
          Math.round(((event.loaded * 100) / event.total) * 100) / 100;
        onProgress(progress);
      }
    });

    // Handle load event (successful upload)
    xhr.addEventListener("load", () => {
      if (xhr.status === 204) {
        // HTTP 204 means success for S3 POST

        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Handle error
    xhr.addEventListener("error", () => {
      reject(new Error("Upload failed"));
    });

    // Handle abort
    xhr.addEventListener("abort", () => {
      reject(new Error("Upload aborted"));
    });

    // Handle abort signal
    signal.addEventListener(
      "abort",
      () => {
        xhr.abort();
      },
      { once: true }
    );

    // Open connection to the S3 presigned POST URL
    xhr.open("POST", url);

    // Create FormData and append the fields and file
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", file);

    // Send the FormData
    xhr.send(formData);
    xhr.addEventListener(
      "loadend",
      () => {
        // Always clean up
        xhr.abort();
      },
      { once: true }
    );
  });
};

export const isMobileScreen = window.innerWidth < 768;

export const getVendorTimezone = (): string => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone;
};

export const replaceVideoUrl = (url: string) => {
  const newUrl = url.replace(CDN_URL, S3_BASE_URL);
  return newUrl;
};

interface LoadingStep {
  start: number;
  end: number;
  delay: number;
}

export const runLoadingProgress = async (
  setProgress: (progress: number) => void,
  steps: LoadingStep[]
): Promise<void> => {
  for (const step of steps) {
    for (let i = step.start; i <= step.end; i++) {
      setProgress(i);
      await new Promise((resolve) => setTimeout(resolve, step.delay));
    }
  }
};
